import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CardCase,
  CaseInbox,
  CaseEvent,
  CaseStatus,
  ClosedReason,
  Trip,
} from '@taksikortti/shared';
import { api } from './client';

export interface CasesListParams {
  status?: CaseStatus[];
  flagged?: boolean;
  q?: string;
  page?: number;
  pageSize?: number;
}

export interface CasesListResponse {
  rows: CaseInbox[];
  page: number;
  pageSize: number;
  total: number;
}

function toQuery(p: CasesListParams): string {
  const u = new URLSearchParams();
  for (const s of p.status ?? []) u.append('status', s);
  if (p.flagged) u.set('flagged', 'true');
  if (p.q) u.set('q', p.q);
  if (p.page != null) u.set('page', String(p.page));
  if (p.pageSize != null) u.set('pageSize', String(p.pageSize));
  const s = u.toString();
  return s ? `?${s}` : '';
}

export function useCasesList(params: CasesListParams) {
  return useQuery({
    queryKey: ['cases', 'list', params],
    queryFn: () => api<CasesListResponse>(`/cases${toQuery(params)}`),
    staleTime: 30_000,
  });
}

export interface CaseDetailResponse {
  case: CardCase;
  etag: string;
  trips: Trip[];
  aggregates: {
    billable: number;
    refund: number;
    eiLaskuteta: number;
    closedTrip: number;
    wrongRemaining: number;
    firstCharge: string | null;
    lastCharge: string | null;
  };
  timeline: CaseEvent[];
  previousIncidents: Array<{
    caseId: string;
    status: CaseStatus;
    closedReason: ClosedReason | null;
    closedAt: string | null;
    createdAt: string;
    notesPreview: string;
  }>;
  window: { start: string | null; end: string | null };
}

export function useCaseDetail(korttinumero: string, caseId: string) {
  return useQuery({
    queryKey: ['cases', 'detail', korttinumero, caseId],
    queryFn: () => api<CaseDetailResponse>(`/cases/${encodeURIComponent(korttinumero)}/${encodeURIComponent(caseId)}`),
    staleTime: 15_000,
  });
}

// Note: the API also supports dormant 'reassign' — removed from the UI
// (kertis-style shared queue, no per-user assignment).
export type CaseAction =
  | { kind: 'send_query' }
  | { kind: 'send_reminder' }
  | { kind: 'close'; reason: ClosedReason }
  | { kind: 'reopen' }
  | { kind: 'flag'; flagged: boolean }
  | { kind: 'edit_notes'; notes: string };

export interface ApplyActionResponse {
  caseId: string;
  fromStatus: CaseStatus;
  toStatus: CaseStatus;
  newEtag: string;
}

export function useApplyAction(korttinumero: string, caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ etag, action }: { etag: string; action: CaseAction }) =>
      api<ApplyActionResponse>(
        `/cases/${encodeURIComponent(korttinumero)}/${encodeURIComponent(caseId)}/actions`,
        { method: 'POST', body: { etag, action } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cases'] });
      qc.invalidateQueries({ queryKey: ['worklist'] }); // list rows mirror case status/notes
    },
  });
}

export type TripAction =
  | { kind: 'send_query' }
  | { kind: 'send_reminder' }
  | { kind: 'close'; reason: ClosedReason; notes?: string }
  | { kind: 'reopen' }
  | { kind: 'edit_notes'; notes: string };

export interface ApplyTripActionResponse {
  dedupKey: string;
  fromStatus: 'open' | 'query_sent' | 'reminder_sent' | 'closed';
  toStatus: 'open' | 'query_sent' | 'reminder_sent' | 'closed';
  caseStatus: CaseStatus;
  caseClosed: boolean;
  wrongRemaining: number;
}

export function useApplyTripAction(korttinumero: string, caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dedupKey, action }: { dedupKey: string; action: TripAction }) =>
      api<ApplyTripActionResponse>(
        `/cases/${encodeURIComponent(korttinumero)}/${encodeURIComponent(caseId)}/trips/${encodeURIComponent(dedupKey)}/actions`,
        { method: 'POST', body: { action } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cases'] });
      qc.invalidateQueries({ queryKey: ['worklist'] }); // trip actions touch the case row too
    },
  });
}
